import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';

const API = process.env.REACT_APP_BACKEND_URL;

function HolidayManagement({ user, onLogout }) {
  const [holidays, setHolidays] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingHoliday, setEditingHoliday] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  
  const [formData, setFormData] = useState({
    name: '',
    date: '',
    type: 'public',
    description: '',
    applicable_locations: ['All']
  });

  const holidayTypes = ['public', 'optional', 'restricted'];
  const locations = ['All', 'Mumbai', 'Delhi', 'Bangalore', 'Hyderabad', 'Chennai', 'Pune'];

  useEffect(() => {
    fetchHolidays();
  }, [selectedYear]);

  const fetchHolidays = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/holidays/?year=${selectedYear}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setHolidays(response.data.holidays || []);
    } catch (error) {
      console.error('Error fetching holidays:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const token = localStorage.getItem('token');
      
      if (editingHoliday) {
        await axios.put(
          `${API}/api/holidays/${editingHoliday.id}`,
          formData,
          { headers: { Authorization: `Bearer ${token}` }}
        );
        alert('Holiday updated successfully!');
      } else {
        await axios.post(
          `${API}/api/holidays/`,
          formData,
          { headers: { Authorization: `Bearer ${token}` }}
        );
        alert('Holiday created successfully!');
      }
      
      resetForm();
      fetchHolidays();
    } catch (error) {
      alert('Error: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (holidayId) => {
    if (!window.confirm('Are you sure you want to delete this holiday?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/api/holidays/${holidayId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert('Holiday deleted successfully!');
      fetchHolidays();
    } catch (error) {
      alert('Error: ' + (error.response?.data?.detail || error.message));
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      date: '',
      type: 'public',
      description: '',
      applicable_locations: ['All']
    });
    setEditingHoliday(null);
    setShowForm(false);
  };

  const handleEdit = (holiday) => {
    setFormData({
      name: holiday.name,
      date: holiday.date,
      type: holiday.type,
      description: holiday.description || '',
      applicable_locations: holiday.applicable_locations
    });
    setEditingHoliday(holiday);
    setShowForm(true);
  };

  const toggleLocation = (location) => {
    const current = formData.applicable_locations;
    if (current.includes(location)) {
      if (current.length > 1) {
        setFormData({...formData, applicable_locations: current.filter(l => l !== location)});
      }
    } else {
      setFormData({...formData, applicable_locations: [...current, location]});
    }
  };

  const getTypeColor = (type) => {
    switch(type) {
      case 'public': return 'bg-green-100 text-green-800';
      case 'optional': return 'bg-blue-100 text-blue-800';
      case 'restricted': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getTypeIcon = (type) => {
    switch(type) {
      case 'public': return '🎉';
      case 'optional': return '🎯';
      case 'restricted': return '⚠️';
      default: return '📅';
    }
  };

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="p-6">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Holiday Management</h1>
            <p className="mt-2 text-gray-600">Manage public, optional, and restricted holidays</p>
          </div>
          <div className="flex gap-3">
            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(parseInt(e.target.value))}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2b3380]"
            >
              {[2024, 2025, 2026, 2027].map(year => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
            <button
              onClick={() => setShowForm(true)}
              className="px-6 py-3 bg-[#2b3380] text-white rounded-lg hover:bg-[#1f2660] transition"
            >
              + Add Holiday
            </button>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-green-50 rounded-lg p-4 border border-green-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-green-600 font-medium">Public Holidays</p>
                <p className="text-2xl font-bold text-green-900">
                  {holidays.filter(h => h.type === 'public').length}
                </p>
              </div>
              <span className="text-3xl">🎉</span>
            </div>
          </div>
          <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-blue-600 font-medium">Optional Holidays</p>
                <p className="text-2xl font-bold text-blue-900">
                  {holidays.filter(h => h.type === 'optional').length}
                </p>
              </div>
              <span className="text-3xl">🎯</span>
            </div>
          </div>
          <div className="bg-yellow-50 rounded-lg p-4 border border-yellow-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-yellow-600 font-medium">Restricted Holidays</p>
                <p className="text-2xl font-bold text-yellow-900">
                  {holidays.filter(h => h.type === 'restricted').length}
                </p>
              </div>
              <span className="text-3xl">⚠️</span>
            </div>
          </div>
        </div>

        {/* Holidays Table */}
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          {holidays.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <p className="mb-4">No holidays configured for {selectedYear}</p>
              <button
                onClick={() => setShowForm(true)}
                className="px-6 py-2 bg-[#2b3380] text-white rounded-lg hover:bg-[#1f2660] transition"
              >
                Add First Holiday
              </button>
            </div>
          ) : (
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Holiday</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Locations</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {holidays.map((holiday) => (
                  <tr key={holiday.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="flex items-center">
                        <span className="text-2xl mr-3">{getTypeIcon(holiday.type)}</span>
                        <div>
                          <div className="text-sm font-medium text-gray-900">{holiday.name}</div>
                          {holiday.description && (
                            <div className="text-xs text-gray-500">{holiday.description}</div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {new Date(holiday.date).toLocaleDateString('en-US', { 
                        weekday: 'short', 
                        year: 'numeric', 
                        month: 'short', 
                        day: 'numeric' 
                      })}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${getTypeColor(holiday.type)}`}>
                        {holiday.type.charAt(0).toUpperCase() + holiday.type.slice(1)}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">
                      {holiday.applicable_locations?.join(', ')}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <button
                        onClick={() => handleEdit(holiday)}
                        className="text-blue-600 hover:text-blue-900 mr-3"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(holiday.id)}
                        className="text-red-600 hover:text-red-900"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Create/Edit Holiday Modal */}
        {showForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-2xl font-bold text-gray-900">
                    {editingHoliday ? 'Edit Holiday' : 'Add Holiday'}
                  </h2>
                  <button onClick={resetForm} className="text-gray-500 hover:text-gray-700 text-2xl">×</button>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Holiday Name *</label>
                    <input
                      type="text"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2b3380]"
                      placeholder="e.g., Diwali, Republic Day"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Date *</label>
                    <input
                      type="date"
                      required
                      value={formData.date}
                      onChange={(e) => setFormData({...formData, date: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2b3380]"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Type *</label>
                    <div className="grid grid-cols-3 gap-2">
                      {holidayTypes.map(type => (
                        <button
                          key={type}
                          type="button"
                          onClick={() => setFormData({...formData, type})}
                          className={`px-4 py-2 rounded-lg border-2 transition ${
                            formData.type === type
                              ? 'border-[#2b3380] bg-[#2b3380] text-white'
                              : 'border-gray-300 hover:border-gray-400'
                          }`}
                        >
                          {type.charAt(0).toUpperCase() + type.slice(1)}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                    <textarea
                      value={formData.description}
                      onChange={(e) => setFormData({...formData, description: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2b3380]"
                      rows="2"
                      placeholder="Additional details (optional)"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Applicable Locations *</label>
                    <div className="grid grid-cols-3 gap-2">
                      {locations.map(location => (
                        <label key={location} className="flex items-center">
                          <input
                            type="checkbox"
                            checked={formData.applicable_locations.includes(location)}
                            onChange={() => toggleLocation(location)}
                            className="mr-2"
                          />
                          <span className="text-sm">{location}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  <div className="flex gap-3 pt-4">
                    <button
                      type="button"
                      onClick={resetForm}
                      className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={loading}
                      className="flex-1 px-6 py-3 bg-[#2b3380] text-white rounded-lg hover:bg-[#1f2660] transition disabled:bg-gray-400"
                    >
                      {loading ? 'Saving...' : editingHoliday ? 'Update' : 'Create'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}

export default HolidayManagement;